import { Lock, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";

/** Displayed when the current tenant's plan doesn't include the requested module.
 *  Used both proactively (before hitting API) and reactively (after a 402 response). */
export function PlanNotIncluded({ module, testId }: { module: string; testId?: string }) {
    const { t } = useTranslation();
    const { plan } = useAuth();
    return (
        <div
            data-testid={testId || `plan-not-included-${module}`}
            className="surface-card p-8 max-w-2xl mx-auto text-center rounded-xl border"
            style={{ background: "var(--card)", borderColor: "var(--border)" }}
        >
            <div className="mx-auto rounded-full flex items-center justify-center mb-4" style={{ width: 56, height: 56, background: "var(--selected-bg)" }}>
                <Lock size={26} style={{ color: "var(--primary)" }} aria-hidden="true" />
            </div>
            <h2 className="text-lg font-semibold mb-2" style={{ color: "var(--text)" }}>
                {t("plan.module_not_included.title", { module: t(`plan.module.${module}`, { defaultValue: module }) })}
            </h2>
            <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
                {t("plan.module_not_included.desc", { plan: plan?.name || "—" })}
            </p>
            <Link
                to="/settings/subscription"
                data-testid={`plan-upgrade-${module}`}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium"
                style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}
            >
                <Sparkles size={16} aria-hidden="true" />
                {t("plan.upgrade_cta")}
            </Link>
        </div>
    );
}

/** Wraps a page: shows PlanNotIncluded when the plan lacks the module, otherwise renders children. */
export function ModuleGate({ module, children }: { module: string; children: React.ReactNode }) {
    const { hasModule, me } = useAuth();
    if (!me) return null;
    if (!me.user.is_platform_owner && !hasModule(module)) {
        return <PlanNotIncluded module={module} />;
    }
    return <>{children}</>;
}
